// Filtro de rango de fechas + línea (y planta si hay más de una) para las
// lecturas de sobrellenado y cambios de referencia. Estado en la URL
// (?desde&hasta&planta&linea) para que el enlace se pueda compartir.
//
// Rango por defecto: últimos 7 días. El API exige desde ≤ hasta y máximo 92
// días; se valida aquí antes de consultar.

import { useSearchParams } from 'react-router-dom';
import { INGESTA_RANGO_MAX_DIAS } from 'schemas';
import { Input } from '../../../components/ui/input';
import { Select } from '../../../components/ui/select';

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

function isoLocal(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${dd}`;
}

/** [hoy − 6, hoy] en fechas locales. */
export function rangoUltimos7(hoy = new Date()): { desde: string; hasta: string } {
  const desde = new Date(hoy);
  desde.setDate(hoy.getDate() - 6);
  return { desde: isoLocal(desde), hasta: isoLocal(hoy) };
}

export function diasRango(desde: string, hasta: string): number {
  return (Date.parse(`${hasta}T00:00:00Z`) - Date.parse(`${desde}T00:00:00Z`)) / 86_400_000;
}

/** Mensaje de error del rango, o null si es válido. */
export function errorRango(desde: string, hasta: string): string | null {
  if (!ISO_RE.test(desde) || !ISO_RE.test(hasta)) return 'Elija las dos fechas.';
  const dias = diasRango(desde, hasta);
  if (dias < 0) return 'La fecha inicial debe ser anterior o igual a la final.';
  if (dias > INGESTA_RANGO_MAX_DIAS)
    return `El rango no puede superar ${INGESTA_RANGO_MAX_DIAS} días.`;
  return null;
}

export interface FiltrosRango {
  desde: string;
  hasta: string;
  plantaId: string | null;
  lineaId: string | null;
}

export function useFiltrosRango(): [FiltrosRango, (patch: Partial<FiltrosRango>) => void] {
  const [params, setParams] = useSearchParams();
  const def = rangoUltimos7();
  const desde = params.get('desde') ?? def.desde;
  const hasta = params.get('hasta') ?? def.hasta;
  const filtros: FiltrosRango = {
    desde,
    hasta,
    plantaId: params.get('planta') || null,
    lineaId: params.get('linea') || null,
  };
  const set = (patch: Partial<FiltrosRango>) => {
    const next = new URLSearchParams(params);
    const mapa: [keyof FiltrosRango, string][] = [
      ['desde', 'desde'],
      ['hasta', 'hasta'],
      ['plantaId', 'planta'],
      ['lineaId', 'linea'],
    ];
    for (const [k, qp] of mapa) {
      if (!(k in patch)) continue;
      const v = patch[k];
      if (v === null || v === undefined || v === '') next.delete(qp);
      else next.set(qp, v);
    }
    setParams(next, { replace: true });
  };
  return [filtros, set];
}

export interface OpcionFiltro {
  id: string;
  nombre: string;
}

export function FiltroRango({
  filtros,
  onChange,
  plantas,
  lineas,
}: {
  filtros: FiltrosRango;
  onChange: (patch: Partial<FiltrosRango>) => void;
  /** Solo se muestra el selector si hay más de una planta. */
  plantas: OpcionFiltro[];
  lineas: (OpcionFiltro & { plantaId: string })[];
}) {
  const lineasVisibles = filtros.plantaId
    ? lineas.filter((l) => l.plantaId === filtros.plantaId)
    : lineas;
  const error = errorRango(filtros.desde, filtros.hasta);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-[11px] text-fg-tertiary">
          Desde
          <Input
            type="date"
            value={filtros.desde}
            max={filtros.hasta}
            onChange={(e) => onChange({ desde: e.target.value })}
            className="h-9 w-40"
            aria-invalid={error ? true : undefined}
          />
        </label>
        <label className="flex flex-col gap-1 text-[11px] text-fg-tertiary">
          Hasta
          <Input
            type="date"
            value={filtros.hasta}
            min={filtros.desde}
            onChange={(e) => onChange({ hasta: e.target.value })}
            className="h-9 w-40"
            aria-invalid={error ? true : undefined}
          />
        </label>
        {plantas.length > 1 ? (
          <label className="flex flex-col gap-1 text-[11px] text-fg-tertiary">
            Planta
            <Select
              value={filtros.plantaId ?? ''}
              onChange={(e) => onChange({ plantaId: e.target.value || null, lineaId: null })}
              className="h-9 w-48"
            >
              <option value="">Todas</option>
              {plantas.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </Select>
          </label>
        ) : null}
        {lineasVisibles.length > 0 ? (
          <label className="flex flex-col gap-1 text-[11px] text-fg-tertiary">
            Línea
            <Select
              value={filtros.lineaId ?? ''}
              onChange={(e) => onChange({ lineaId: e.target.value || null })}
              className="h-9 w-56"
            >
              <option value="">Todas</option>
              {lineasVisibles.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.nombre}
                </option>
              ))}
            </Select>
          </label>
        ) : null}
        <button
          type="button"
          className="h-9 px-1 text-[11px] text-fg-secondary underline underline-offset-2 hover:text-fg"
          onClick={() => onChange({ ...rangoUltimos7() })}
        >
          Últimos 7 días
        </button>
      </div>
      {error ? (
        <p role="alert" className="text-xs text-crit">
          {error}
        </p>
      ) : null}
    </div>
  );
}
